import React, { useRef, useEffect, KeyboardEvent } from 'react';
import { Input, Text } from '@billda/design-system';

interface CodeInputFieldProps {
  /** The current code value */
  value: string;
  /** Callback when code changes */
  onChange: (code: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to show an error state */
  error?: boolean;
  /** Error message to display */
  helperText?: string;
  /** Number of digits for the code */
  length?: number;
  /** Auto-focus the first input */
  autoFocus?: boolean;
  /** Called when all digits are entered */
  onComplete?: (code: string) => void;
  /** Label for accessibility */
  label?: string;
}

const digitInputClass =
  'w-14 text-center text-[1.5rem] font-semibold uppercase placeholder:text-content-disabled';

/**
 * A clean, accessible component for entering magic codes with individual digit inputs
 */
export const CodeInputField: React.FC<CodeInputFieldProps> = ({
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText,
  length = 6,
  autoFocus = false,
  onComplete,
  label = 'Verification Code',
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Ensure we have the right number of input refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Auto-focus first input if requested
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Trigger onComplete when code is full - use a more stable approach
  const onCompleteRef = useRef(onComplete);
  const isCompleteRef = useRef(false);

  // Update the ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const isComplete = value.length === length;

    // Only call onComplete if we just became complete (not already complete)
    if (isComplete && !isCompleteRef.current && onCompleteRef.current) {
      isCompleteRef.current = true;
      onCompleteRef.current(value);
    } else if (!isComplete) {
      isCompleteRef.current = false;
    }
  }, [value, length]);

  const handleInputChange = (index: number, inputValue: string) => {
    try {
      // Allow alphanumeric characters (letters and digits)
      const char = inputValue.replace(/[^a-zA-Z0-9]/g, '').slice(-1);

      // Update the code value with proper bounds checking
      const newCode = value.split('');

      // Ensure we have a valid array and index
      if (index >= 0 && index < length) {
        newCode[index] = char;
      }

      // Pad with empty strings if needed
      while (newCode.length < length) {
        newCode.push('');
      }

      const updatedCode = newCode.join('').slice(0, length);
      onChange(updatedCode);

      // Move to next input if character was entered
      if (char && index < length - 1) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    } catch (error) {
      console.error('Error in handleInputChange:', error);
      // Fallback: just update with the safe character
      const char = inputValue.replace(/[^a-zA-Z0-9]/g, '').slice(-1);
      if (char) {
        onChange(value.substring(0, index) + char + value.substring(index + 1));
      }
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent) => {
    try {
      if (event.key === 'Backspace') {
        event.preventDefault();

        const newCode = value.split('');

        if (newCode[index]) {
          // Clear current digit
          newCode[index] = '';
        } else if (index > 0) {
          // Move to previous input and clear it
          newCode[index - 1] = '';
          const prevInput = inputRefs.current[index - 1];
          if (prevInput) {
            prevInput.focus();
          }
        }

        while (newCode.length < length) {
          newCode.push('');
        }

        onChange(newCode.join('').slice(0, length));
      } else if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      } else if (event.key === 'ArrowRight' && index < length - 1) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    } catch (error) {
      console.error('Error in handleKeyDown:', error);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    try {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData('text')
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, length);
      onChange(pastedData);

      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      const targetInput = inputRefs.current[nextIndex];
      if (targetInput) {
        targetInput.focus();
      }
    } catch (error) {
      console.error('Error in handlePaste:', error);
    }
  };

  // Create digits array with safety checks
  const digits = React.useMemo(() => {
    try {
      const valueArray = value ? value.split('') : [];
      const result = [];
      for (let i = 0; i < length; i++) {
        result[i] = valueArray[i] || '';
      }
      return result;
    } catch (error) {
      console.error('Error creating digits array:', error);
      return Array(length).fill('');
    }
  }, [value, length]);

  const renderDigitInput = (index: number) => (
    <Input
      key={index}
      ref={el => {
        inputRefs.current[index] = el;
      }}
      value={digits[index] || ''}
      onChange={e => handleInputChange(index, e.target.value)}
      onKeyDown={e => handleKeyDown(index, e)}
      disabled={disabled}
      invalid={error}
      maxLength={1}
      aria-label={`Character ${index + 1} of ${length}`}
      data-testid={`magic-code-input-${index + 1}`}
      className={digitInputClass}
    />
  );

  return (
    <div>
      {/* Screen reader label */}
      <label
        className={`mb-2 block type-body-md font-medium ${
          error ? 'text-content-error' : 'text-content-primary'
        }`}
      >
        {label}
      </label>
      {/* Code input fields */}
      <div
        className={`flex items-center justify-center gap-2 ${
          helperText ? 'mb-2' : 'mb-0'
        }`}
        onPaste={handlePaste}
      >
        {/* First group of 3 characters */}
        {Array.from({ length: 3 }, (_, index) => renderDigitInput(index))}

        {/* Dash separator */}
        <Text
          variant="h5"
          color="secondary"
          className="mx-2 font-bold"
          aria-hidden="true"
        >
          -
        </Text>

        {/* Second group of 3 characters */}
        {Array.from({ length: 3 }, (_, index) => renderDigitInput(index + 3))}
      </div>
      {/* Helper text */}
      {helperText && (
        <Text
          as="div"
          variant="caption"
          className={`mt-1 block text-center ${
            error ? 'text-content-error' : 'text-content-secondary'
          }`}
        >
          {helperText}
        </Text>
      )}
    </div>
  );
};

export default CodeInputField;
